import { InfoCircleOutlined } from "@ant-design/icons";
import {
    Badge,
    Button,
    DatePicker,
    Flex,
    Input,
    Select,
    Tag,
    Tooltip,
    TreeSelect,
    Typography,
    message,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mapZnsAPIWithErrorHandling } from "../../../api/zns/zns";
import { collectionAPIWithErrorHandling } from "../../../api/collection/collection";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ExportExcelButton from "../../../components/common/ExportExcelButton";
import ListTableCard from "../../../components/common/ListTableCard";
import ListToolbar, {
    selectVisibleColumns,
    useListToolbar,
} from "../../../components/common/ListToolbar";
import {
    ERROR_REASON_OPTIONS,
    dlvrLabel,
    gatewayLabel,
    getCategory,
    getDetailColor,
} from "../../../constants/znsErrorCodes";
import { brand, typography } from "../../../theme/tokens";

const { Text } = Typography;

const TYPE_OPTIONS = [
    { value: "ZNS", label: "ZNS" },
    { value: "SMS", label: "SMS" },
];

const SEND_STATUS_OPTIONS = [
    { value: "SUCCESS", label: "Thành công" },
    { value: "FAILED", label: "Thất bại" },
    { value: "UNDELIVERED", label: "Không nhận được" },
    { value: "PENDING", label: "Đang chờ báo cáo" },
];

const DEFAULT_VISIBLE_FILTERS = [];
// const DEFAULT_VISIBLE_COLUMNS = [
//     "collection_name", "referentid", "type", "content",
//     "send_status", "collection_type", "dlvr", "created_at",
// ];

const DEFAULT_VISIBLE_COLUMNS = [
    "charge_target_number", "referentid", "type", "content",
    "send_status", "dlvr", "created_at"
];

/**
 * Tài sản nhập từ Excel đôi khi lẫn xuống dòng hoặc chuỗi "\n" theo nghĩa đen — gộp về
 * một dòng. Cùng cách với màn Lịch sử thanh toán và file Excel.
 */
const assetText = (value) => String(value ?? "").replace(/(\\n|\s)+/g, " ").trim();

/** Cây đơn vị → dạng phẳng cho TreeSelect treeDataSimpleMode (cùng cách màn Thanh toán). */
const flattenOrgTree = (nodes, parentId = 0) =>
    nodes.flatMap((node) => [
        { id: node.id, pId: parentId, value: node.id, title: node.name, isLeaf: !node.children_recursive?.length },
        ...flattenOrgTree(node.children_recursive || [], node.id),
    ]);

const EMPTY_FILTERS = {
    search: "", type: undefined, collection_id: undefined, organization_id: undefined,
    send_status: undefined, error_reason: undefined, from_date: undefined, to_date: undefined,
};

export default function ZnsHistories() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [collections, setCollections] = useState([]);
    const [orgTree, setOrgTree] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });

    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const {
        visibleFilters, setVisibleFilters,
        visibleColumns, setVisibleColumns,
    } = useListToolbar({
        defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
        defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    });
    const searchTimer = useRef(null);

    const loadData = useCallback(async (overrides = {}) => {
        const page = overrides.page ?? pagination.current;
        const per_page = overrides.per_page ?? pagination.pageSize;
        const f = { ...filters, ...overrides.filters };
        try {
            setLoading(true);
            const res = await mapZnsAPIWithErrorHandling.getHistories({
                page,
                per_page,
                search: f.search,
                type: f.type,
                collection_id: f.collection_id,
                organization_id: f.organization_id,
                send_status: f.send_status,
                error_reason: f.error_reason,
                from_date: f.from_date,
                to_date: f.to_date,
            });
            if (res.data?.success) {
                const d = res.data.data;
                setData(d.data || []);
                setPagination((p) => ({ ...p, current: d.current_page, total: d.total, pageSize: per_page }));
            }
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, pagination.current, pagination.pageSize]);

    const fetchCollections = useCallback(async () => {
        try {
            const response = await collectionAPIWithErrorHandling.getCollections({
                page: 1,
                per_page: 999,
                sort_by: "created_at",
                sort_order: "desc",
                parent_id: null,
                type_child: "PARENT",
            });
            if (response.data?.success) {
                setCollections(response.data.data?.data || []);
            }
        } catch (error) {
            console.error("Không thể tải danh sách đợt thu:", error.message);
        }
    }, []);

    const fetchOrgTree = useCallback(async () => {
        try {
            const res = await organizationAPIWithErrorHandling.getTreeStructure();
            if (res.success) setOrgTree(flattenOrgTree(res.data || []));
        } catch (error) {
            setOrgTree([]);
        }
    }, []);

    useEffect(() => {
        loadData({ page: 1 });
        fetchCollections();
        fetchOrgTree();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Áp bộ lọc -> về trang 1
    const applyFilter = (patch) => {
        setFilters((f) => ({ ...f, ...patch }));
        setPagination((p) => ({ ...p, current: 1 }));
        loadData({ page: 1, filters: patch });
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setFilters((f) => ({ ...f, search: value }));
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setPagination((p) => ({ ...p, current: 1 }));
            loadData({ page: 1, filters: { search: value } });
        }, 500);
    };

    const handleTableChange = (pg) => {
        setPagination((p) => ({ ...p, current: pg.current, pageSize: pg.pageSize }));
        loadData({ page: pg.current, per_page: pg.pageSize });
    };

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        setPagination((p) => ({ ...p, current: 1 }));
        loadData({ page: 1, filters: EMPTY_FILTERS });
    };

    // ===== Render helpers =====
    const renderSendStatus = (record) => {
        const s = record?.send_status;
        if (s === "SUCCESS") return <Badge status="success" text="Thành công" />;
        if (s === "FAILED") return <Badge status="error" text="Thất bại" />;
        if (s === "UNDELIVERED") return <Badge status="warning" text="Không nhận được" />;
        if (s === "PENDING") return <Badge status="processing" text="Đang chờ báo cáo" />;
        return <Badge status="default" text="Chưa xác định" />;
    };

    const renderMessageSnapshot = (record) => {
        if (record?.message_sms) return record.message_sms;
        if (record?.meta_data) {
            try {
                return JSON.stringify(record.meta_data);
            } catch (error) {
                return String(record.meta_data);
            }
        }
        return "-";
    };

    // Chi tiết trạng thái: ưu tiên báo cáo giao (dlvr_status); nếu chưa có + gửi
    // lỗi thì lấy theo mã gateway. Mã lạ thì fallback về `receive_result` (text
    // gốc Zalo trả) chứ không hiển thị "Không xác định".
    const renderDetail = (record) => {
        const category = getCategory(record);
        const dlvr = record?.dlvr_status;

        if (dlvr !== null && dlvr !== undefined && dlvr !== "") {
            const text = dlvrLabel(dlvr, category) || record?.receive_result || "Không xác định";
            return <span style={{ color: getDetailColor(category, dlvr) }}>{`${dlvr} - ${text}`}</span>;
        }

        const code = record?.code;
        const sStatus = record?.send_status;
        if ((sStatus === "FAILED" || sStatus === "UNDELIVERED") && code !== null && code !== undefined && code !== "") {
            const text = gatewayLabel(code) || record?.receive_result || "Gửi thất bại";
            return <span style={{ color: "#E5065C" }}>{`${code} - ${text}`}</span>;
        }
        return <Text type="secondary">—</Text>;
    };

    // ===== Định nghĩa cột =====
    const columnDefs = useMemo(() => [
        {
            key: "index", label: "STT", fixed: true,
            column: {
                title: "STT", key: "index", width: 60, fixed: "left",
                render: (_, __, i) => (pagination.current - 1) * pagination.pageSize + i + 1,
            },
        },
        {
            key: "customer", label: "Khách hàng", fixed: true,
            column: {
                title: "Khách hàng", key: "customer", width: 220, fixed: "left",
                render: (_, record) => {
                    const c = record?.customer_fee?.customer;
                    if (!c) return <Text type="secondary">—</Text>;
                    // Tên đi trên tin: tên chủ hộ ghi trên khoản thu (tài sản bật cờ), không thì tên khách
                    const feeName = record?.customer_fee?.customer_name;
                    const isChuHo = !!feeName && feeName !== c.name;
                    return (
                        <Flex align="center" gap={8}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: typography.weight.semibold }}>{feeName || c.name}</div>
                                {isChuHo && (
                                    <Text type="secondary" style={{ fontSize: typography.size.sm, display: "block" }}>
                                        Tên chủ hộ · KH: {c.name}
                                    </Text>
                                )}
                                <Text type="secondary" style={{ fontSize: typography.size.sm }}>{c.phone}</Text>
                            </div>
                            <Button
                                type="text" size="small"
                                icon={<InfoCircleOutlined style={{ color: brand.accent }} />}
                                onClick={() => navigate(`/customers/information/${c.id}`)}
                            />
                        </Flex>
                    );
                },
            },
        },
        {
            // Khách nhiều tài sản có nhiều khoản thu — cột này cho biết tin nhắc cho căn nào.
            key: "charge_target_number", label: "Tài sản",
            column: {
                title: "Tài sản", key: "charge_target_number", width: 200,
                render: (_, record) => {
                    const text = assetText(record?.customer_fee?.charge_target_number);
                    return text
                        ? <span style={{ fontSize: typography.size.sm, lineHeight: 1.4, wordBreak: "break-word" }}>{text}</span>
                        : "-";
                },
            },
        },
        {
            key: "collection_name", label: "Tên đợt thu",
            column: {
                title: "Tên đợt thu", key: "collection_name", width: 240,
                render: (_, r) => r?.customer_fee?.collection?.name || "-",
            },
        },
        {
            key: "referentid", label: "Reference ID",
            column: { title: "Reference ID", dataIndex: "referentid", width: 160, render: (t) => t || "-" },
        },
        {
            key: "type", label: "Loại tin",
            column: {
                title: "Loại tin", key: "type", width: 110,
                render: (_, r) => (
                    <Tag color={r?.type === "ZNS" ? "blue" : "geekblue"} style={{ marginInlineEnd: 0 }}>
                        {r?.type || "-"}
                    </Tag>
                ),
            },
        },
        {
            key: "template_id", label: "Template ID",
            column: { title: "Template ID", dataIndex: "template_id", width: 130, render: (t) => t || "-" },
        },
        {
            key: "event", label: "Sự kiện",
            column: { title: "Sự kiện", dataIndex: "event_code", width: 180, render: (t) => t || "-" },
        },
        {
            key: "content", label: "Nội dung / biến",
            column: {
                title: "Nội dung / biến", key: "content", width: 320,
                render: (_, r) => {
                    const t = renderMessageSnapshot(r);
                    return (
                        <Tooltip title={t}>
                            <Text style={{ fontSize: typography.size.sm }}>
                                {t && t.length > 120 ? t.slice(0, 120) + "…" : t}
                            </Text>
                        </Tooltip>
                    );
                },
            },
        },
        {
            key: "send_status", label: "Trạng thái gửi",
            column: {
                title: "Trạng thái gửi", key: "send_status", width: 150, align: "center",
                render: (_, r) => renderSendStatus(r),
            },
        },
        {
            key: "collection_type", label: "Loại đợt",
            column: {
                title: "Loại đợt", key: "collection_type", width: 110, align: "center",
                render: (_, r) =>
                    r?.customer_fee?.collection?.type === "FIXED"
                        ? <Badge status="success" text="Cố định" />
                        : <Badge status="processing" text="Chiến dịch" />,
            },
        },
        {
            key: "dlvr", label: "Chi tiết trạng thái",
            column: {
                title: "Chi tiết trạng thái", key: "dlvr", width: 240,
                render: (_, r) => renderDetail(r),
            },
        },
        {
            key: "receive_result", label: "Lý do gốc (Zalo)",
            column: {
                title: "Lý do gốc (Zalo)", dataIndex: "receive_result", width: 200,
                render: (t) => t || <Text type="secondary">—</Text>,
            },
        },
        {
            key: "created_at", label: "Ngày gửi",
            column: {
                title: "Ngày gửi", key: "created_at", width: 150, align: "center",
                render: (_, r) => {
                    const at = r?.sent_at || r?.created_at;
                    return at ? dayjs(at).format("DD/MM/YYYY HH:mm:ss") : "—";
                },
            },
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [pagination.current, pagination.pageSize, navigate]);

    // ===== Định nghĩa bộ lọc =====
    const filterDefs = [
        {
            key: "date_range", label: "Ngày gửi", span: { xs: 24, sm: 12, md: 7 },
            field: (
                <DatePicker.RangePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    allowClear
                    placeholder={["Từ ngày", "Đến ngày"]}
                    value={[
                        filters.from_date ? dayjs(filters.from_date) : null,
                        filters.to_date ? dayjs(filters.to_date) : null,
                    ]}
                    onChange={(dates) =>
                        applyFilter({
                            from_date: dates?.[0] ? dates[0].format("YYYY-MM-DD") : undefined,
                            to_date: dates?.[1] ? dates[1].format("YYYY-MM-DD") : undefined,
                        })
                    }
                />
            ),
        },
        {
            key: "send_status", label: "Trạng thái gửi", span: { xs: 24, sm: 8, md: 5 },
            field: (
                <Select
                    style={{ width: "100%" }} allowClear placeholder="Trạng thái gửi"
                    value={filters.send_status}
                    onChange={(v) => applyFilter({ send_status: v })}
                    options={SEND_STATUS_OPTIONS}
                />
            ),
        },
        {
            key: "error_reason", label: "Lý do lỗi", span: { xs: 24, sm: 12, md: 7 },
            field: (
                <Select
                    style={{ width: "100%" }} allowClear showSearch placeholder="Lý do không gửi được..."
                    value={filters.error_reason}
                    onChange={(v) => applyFilter({ error_reason: v })}
                    optionFilterProp="label"
                    options={ERROR_REASON_OPTIONS}
                />
            ),
        },
        {
            key: "type", label: "Loại tin", span: { xs: 24, sm: 8, md: 5 },
            field: (
                <Select
                    style={{ width: "100%" }} allowClear placeholder="Loại tin"
                    value={filters.type}
                    onChange={(v) => applyFilter({ type: v })}
                    options={TYPE_OPTIONS}
                />
            ),
        },
        {
            key: "collection_id", label: "Tên đợt thu", span: { xs: 24, sm: 12, md: 7 },
            field: (
                <Select
                    style={{ width: "100%" }} allowClear showSearch placeholder="Chọn đợt thu..."
                    value={filters.collection_id}
                    onChange={(v) => applyFilter({ collection_id: v })}
                    filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={collections.map((c) => ({ value: c.id, label: c.name }))}
                />
            ),
        },
        {
            key: "organization_id", label: "Đơn vị", span: { xs: 24, sm: 12, md: 7 },
            field: (
                <TreeSelect
                    treeDataSimpleMode
                    style={{ width: "100%" }}
                    placeholder="Chọn đơn vị..."
                    value={filters.organization_id}
                    onChange={(v) => applyFilter({ organization_id: v })}
                    treeData={orgTree}
                    styles={{ popup: { root: { maxHeight: 400, overflow: "auto" } } }}
                    showSearch
                    filterTreeNode={(input, item) =>
                        String(item.title).toLowerCase().includes(input.toLowerCase())
                    }
                    allowClear
                />
            ),
        },
        {
            key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 7 },
            field: (
                <Input
                    allowClear
                    placeholder="Tìm theo tên KH / SĐT / tài sản / Reference ID / sự kiện"
                    value={filters.search}
                    onChange={handleSearchChange}
                />
            ),
        },
    ];

    const columns = selectVisibleColumns(columnDefs, visibleColumns);
    const hasActiveFilter = filters.search || filters.type || filters.collection_id || filters.organization_id || filters.send_status || filters.error_reason || filters.from_date || filters.to_date;

    const exportParams = {
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.type ? { type: filters.type } : {}),
        ...(filters.collection_id ? { collection_id: filters.collection_id } : {}),
        ...(filters.organization_id ? { organization_id: filters.organization_id } : {}),
        ...(filters.send_status ? { send_status: filters.send_status } : {}),
        ...(filters.error_reason ? { error_reason: filters.error_reason } : {}),
        ...(filters.from_date ? { from_date: filters.from_date } : {}),
        ...(filters.to_date ? { to_date: filters.to_date } : {}),
    };

    return (
        <BrandedPageLayout
            title="Lịch sử gửi tin"
            subtitle="Theo dõi trạng thái gửi ZNS và SMS: đã đẩy qua hệ thống, đã tới máy khách, hay thất bại."
            breadcrumbItems={[{ title: "Báo cáo gửi tin" }]}
        >
            <ListToolbar
                filterDefs={filterDefs}
                visibleFilters={visibleFilters}
                onChangeVisibleFilters={setVisibleFilters}
                columnDefs={columnDefs}
                visibleColumns={visibleColumns}
                onChangeVisibleColumns={setVisibleColumns}
                defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
                hasActiveFilter={hasActiveFilter}
                onClearFilter={handleReset}
                onReload={() => loadData()}
                extra={
                    <ExportExcelButton
                        url="/api/v1/logs/zns/export"
                        params={exportParams}
                        fileName="lich_su_gui_tin.xlsx"
                        actions={["zns.histories"]}
                    />
                }
            />

            <ListTableCard
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                }}
                onChange={handleTableChange}
                emptyText="Không có lịch sử gửi tin"
            />
        </BrandedPageLayout>
    );
}
